/**
 * Select Product Option Handler
 * Replica of TypeScript-master select-product-option.directive.ts
 * Handles dropdown-style product option selection with availability calculation
 */

class SelectProductOptionHandler {
  constructor(element) {
    this.host = element;
    this.isOpen = false;
    this.isDisabled = false;
    this.isOptionsScrolled = false;
    
    // Parse attributes
    this.option = this.parseOptionData();
    this.layout = this.host.getAttribute('select-product-option-layout') || 'default';
    this.isIndependent = this.host.getAttribute('select-product-option-independent') === 'true';
    this.hideOutOfStock = this.host.getAttribute('hide-out-of-stock') === 'true';
    this.hideUnavailable = this.host.getAttribute('hide-unavailable') === 'true';
    this.hideNotes = this.host.getAttribute('hide-notes') === 'true';
    this.noCalculate = this.host.hasAttribute('no-calc');
    
    const optionCount = parseInt(this.host.getAttribute('option-count') || '0');
    this.gradientEnabled = optionCount > 4;
    
    // Cache DOM elements
    this.container = this.host.querySelector('.selectOption__selectContainer');
    this.trigger = this.host.querySelector('.selectOption__select');
    this.optionsHolder = this.host.querySelector('.selectOption__options');
    this.options = this.host.querySelectorAll('.selectOption__option');
    this.radios = this.host.querySelectorAll('.selectOption__option input[type="radio"]');
    this.valueHolder = this.host.querySelector('.selectOption__select--text');
    this.noteHolder = this.host.querySelector('.selectOption__select .selectOption__note');
    
    // CSS classes
    this.classes = {
      open: 'selectOption--open',
      labelUnavailable: 'selectOption__option--unavailable',
      labelDisabled: 'selectOption__option--disabled',
      labelOutOfStock: 'selectOption__option--outOfStock',
      noteUnavailable: 'selectOption__note--unavailable',
      noteOutOfStock: 'selectOption__note--outOfStock',
      scrolled: 'selectOption--scrolled'
    };
    
    this.init();
  }

  init() {
    if (!this.container || !this.trigger) {
      console.warn('Select product option: Missing required elements');
      return;
    }

    if (!this.gradientEnabled) {
      this.hideGradient();
    }

    // Mark as initialized to prevent duplicate handlers
    if (this.host.hasAttribute('data-select-option-initialized')) {
      return; // Already initialized, don't bind events again
    }
    this.host.setAttribute('data-select-option-initialized', 'true');

    this.bindEvents();
  }

  parseOptionData() {
    try {
      const dataAttr = this.host.getAttribute('select-product-option');
      if (dataAttr) {
        return JSON.parse(dataAttr);
      }
    } catch (err) {
      console.error('Error parsing option data:', err);
    }
    return null;
  }

  bindEvents() {
    this.trigger.addEventListener('click', this.toggle.bind(this));
    
    if (this.layout === 'default') {
      this.options.forEach(option => {
        option.addEventListener('click', this.onRadioClick.bind(this));
      });
      this.radios.forEach(radio => {
        radio.addEventListener('click', this.onRadioClick.bind(this));
        radio.addEventListener('change', this.onRadioChange.bind(this));
      });
    } else if (this.layout === 'cylindo') {
      this.options.forEach(label => {
        label.addEventListener('click', this.onCylindoOptionSelect.bind(this));
      });
    }
    
    this.options.forEach(label => {
      label.addEventListener('keydown', this.onLabelKeyDown.bind(this));
    });
  }

  bindVolatileEvents() {
    this.clickOutsideHandler = this.detectClickOutside.bind(this);
    window.addEventListener('click', this.clickOutsideHandler);
    
    if (this.layout === 'cylindo' && this.gradientEnabled) {
      const cylindoScrollContainer = this.optionsHolder.querySelector('.selectOption__options--cylindo');
      
      if (cylindoScrollContainer) {
        this.scrollHandler = this.onOptionsScrolled.bind(this);
        cylindoScrollContainer.addEventListener('scroll', this.scrollHandler);
      }
    }
  }

  unbindVolatileEvents() {
    if (this.clickOutsideHandler) {
      window.removeEventListener('click', this.clickOutsideHandler);
      this.clickOutsideHandler = null;
    }
    
    if (this.layout === 'cylindo' && this.gradientEnabled) {
      const cylindoScrollContainer = this.optionsHolder.querySelector('.selectOption__options--cylindo');
      
      if (cylindoScrollContainer && this.scrollHandler) {
        cylindoScrollContainer.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    }
  }

  detectClickOutside(event) {
    if (this.container.contains(event.target)) {
      return;
    }
    
    this.close();
  }

  onRadioChange() {
    this.close();
    this.showCurrentValue();
  }

  onRadioClick(event) {
    const target = event.target;
    
    // If clicking the currently selected value, just close
    if (target instanceof HTMLInputElement && target.value === this.getValue()) {
      this.close();
      return;
    }
    
    // If clicking a label, trigger its radio input
    if (target instanceof HTMLLabelElement) {
      const radio = target.querySelector('input[type="radio"]');
      if (radio) {
        radio.click();
      }
    }
  }

  onCylindoOptionSelect(event) {
    event.stopPropagation();
    event.preventDefault();
    
    const targetElement = event.target;
    const parentDirectiveEl = targetElement.closest('.shop-collection__item');
    
    if (!parentDirectiveEl) {
      return;
    }
    
    const parentDirective = parentDirectiveEl.shopCollectionHandler;
    
    let inputRadio = null;
    
    if (targetElement.tagName === 'LABEL') {
      inputRadio = targetElement.querySelector('input[type="radio"]');
    } else if (targetElement.tagName === 'INPUT' && targetElement.type === 'radio') {
      inputRadio = targetElement;
    } else {
      const parentLabel = targetElement.closest('label');
      if (parentLabel) {
        inputRadio = parentLabel.querySelector('input[type="radio"]');
      }
    }
    
    if (!inputRadio) {
      return;
    }
    
    inputRadio.checked = true;
    
    this.onRadioChange();
    
    if (parentDirective) {
      parentDirective.updateImages();
      parentDirective.updateLinks();
      parentDirective.updatePrice();
      parentDirective.updateAddToCart();
    }
  }

  showLabel() {
    if (this.valueHolder && this.option) {
      this.valueHolder.textContent = `Choose ${this.option.name}`;
    }
    this.hideNote();
  }

  hideNote() {
    if (this.noteHolder) {
      this.noteHolder.style.opacity = '0';
    }
  }

  showNote() {
    const checkedOption = this.host.querySelector('input[type="radio"]:checked');
    
    if (!checkedOption || !checkedOption.parentElement || !this.noteHolder) {
      return;
    }
    
    const valueNoteEl = checkedOption.parentElement.querySelector('.selectOption__note');
    if (!valueNoteEl) {
      return;
    }
    
    // Remove all note classes
    this.noteHolder.classList.remove(
      this.classes.noteUnavailable,
      this.classes.noteOutOfStock
    );
    
    // Add appropriate class from the option note
    if (valueNoteEl.classList.contains(this.classes.noteUnavailable)) {
      this.noteHolder.classList.add(this.classes.noteUnavailable);
    } else if (valueNoteEl.classList.contains(this.classes.noteOutOfStock)) {
      this.noteHolder.classList.add(this.classes.noteOutOfStock);
    }
    
    const noteText = valueNoteEl.textContent.trim();
    if (!noteText) {
      this.noteHolder.textContent = '';
    } else {
      this.noteHolder.textContent = `(${noteText.replace(/\./g, '').trim()})`;
    }
    
    this.noteHolder.style.opacity = '1';
  }

  showCurrentValue() {
    const value = this.getValue();
    
    if (value && this.valueHolder) {
      this.valueHolder.textContent = value;
      this.valueHolder.style.opacity = '1';
      this.showNote();
      return;
    }
    
    this.showNote();
    this.showLabel();
  }

  getValue() {
    const checkedRadio = this.host.querySelector('input[type="radio"]:checked');
    return checkedRadio ? checkedRadio.value : undefined;
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  onOptionsScrolled(event) {
    const element = event.target;
    const ratio = element.clientHeight / Math.abs(element.scrollHeight - element.scrollTop);
    const isScrolled = ratio > 0.8;
    
    if (isScrolled && !this.isOptionsScrolled) {
      this.hideGradient();
    } else if (!isScrolled && this.isOptionsScrolled) {
      this.showGradient();
    }
    
    this.isOptionsScrolled = isScrolled;
  }

  showGradient() {
    this.host.classList.remove(this.classes.scrolled);
  }

  hideGradient() {
    this.host.classList.add(this.classes.scrolled);
  }

  onLabelKeyDown(event) {
    if (event.key === 'Enter') {
      event.target?.click();
    }
  }

  open() {
    // Prevent opening if already open
    if (this.isOpen) {
      return;
    }
    
    if (!this.noCalculate) {
      this.showLabel();
    }
    
    this.animateOpen();
    this.ariaEnable();
    this.host.classList.add(this.classes.open);
    this.isOpen = true;
    this.bindVolatileEvents();
    this.updateShopCollectionForm();
  }

  close() {
    // Prevent closing if already closed
    if (!this.isOpen) {
      return;
    }
    
    if (!this.noCalculate) {
      this.showCurrentValue();
    }
    
    this.animateClose();
    this.ariaDisable();
    this.host.classList.remove(this.classes.open);
    this.isOpen = false;
    this.unbindVolatileEvents();
    this.updateShopCollectionForm();
  }

  animateOpen() {
    if (!this.optionsHolder) return;
    
    // Set initial state
    this.optionsHolder.style.height = '0';
    this.optionsHolder.style.pointerEvents = 'all';
    this.optionsHolder.style.overflow = 'hidden';
    this.optionsHolder.style.opacity = '0';
    
    this.host.style.setProperty('--border-color', 'var(--color-text)');
    
    if (this.trigger) {
      this.trigger.style.borderBottom = 'none';
      this.trigger.style.borderRadius = '4px 4px 0 0';
    }
    
    // Animate to full height
    requestAnimationFrame(() => {
      const fullHeight = this.optionsHolder.scrollHeight;
      this.optionsHolder.style.transition = 'height 0.55s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.55s cubic-bezier(0.16, 1, 0.3, 1)';
      this.optionsHolder.style.height = `${fullHeight}px`;
      this.optionsHolder.style.opacity = '1';
      
      // Set to auto after animation
      setTimeout(() => {
        if (this.isOpen) {
          this.optionsHolder.style.height = 'auto';
        }
      }, 550);
    });
  }

  animateClose() {
    if (!this.optionsHolder) return;
    
    // Get current height
    const currentHeight = this.optionsHolder.scrollHeight;
    this.optionsHolder.style.height = `${currentHeight}px`;
    this.optionsHolder.style.overflow = 'hidden';
    
    this.host.style.setProperty('--border-color', 'var(--color-border)');
    
    if (this.trigger) {
      this.trigger.style.borderBottom = '';
      this.trigger.style.borderRadius = '';
    }
    
    // Animate to 0
    requestAnimationFrame(() => {
      this.optionsHolder.style.transition = 'height 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)';
      this.optionsHolder.style.height = '0';
      this.optionsHolder.style.opacity = '0';
      this.optionsHolder.style.pointerEvents = 'none';
    });
  }

  ariaEnable() {
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'true');
    }
    if (this.optionsHolder) {
      this.optionsHolder.setAttribute('aria-hidden', 'false');
    }
    this.options.forEach(optionLabel => {
      optionLabel.tabIndex = 0;
    });
  }

  ariaDisable() {
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    if (this.optionsHolder) {
      this.optionsHolder.setAttribute('aria-hidden', 'true');
    }
    this.options.forEach(optionLabel => {
      optionLabel.tabIndex = -1;
    });
    
    if (this.host.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  disableOption(optionLabel, isUnavailable, isOutOfStock = false) {
    const input = optionLabel.querySelector('input[type="radio"]');
    const note = optionLabel.querySelector('.selectOption__note');
    
    if (input) {
      input.disabled = true;
    }
    
    optionLabel.classList.add(this.classes.labelDisabled);
    
    // Remove all label and note classes
    optionLabel.classList.remove(
      this.classes.labelUnavailable,
      this.classes.labelOutOfStock
    );
    
    if (note) {
      note.classList.remove(
        this.classes.noteUnavailable,
        this.classes.noteOutOfStock
      );
    }
    
    if (isUnavailable) {
      if (this.hideUnavailable) {
        optionLabel.setAttribute('hidden', '');
      }
      optionLabel.classList.add(this.classes.labelUnavailable);
      if (note) {
        note.classList.add(this.classes.noteUnavailable);
        note.textContent = 'Unavailable';
      }
    } else if (isOutOfStock) {
      optionLabel.classList.add(this.classes.labelOutOfStock);
      if (note) {
        note.classList.add(this.classes.noteOutOfStock);
        note.textContent = 'Out of Stock';
      }
      if (this.hideOutOfStock) {
        optionLabel.setAttribute('hidden', '');
      }
    } else {
      if (note) {
        note.textContent = '';
      }
    }
    
    // If dropdown is open, ensure tabindex is set correctly even for disabled options
    if (this.isOpen) {
      optionLabel.tabIndex = 0;
    }
  }

  enableOption(optionLabel) {
    const input = optionLabel.querySelector('input[type="radio"]');
    const note = optionLabel.querySelector('.selectOption__note');
    
    if (input) {
      input.disabled = false;
    }
    
    if (note) {
      note.classList.remove(
        this.classes.noteUnavailable,
        this.classes.noteOutOfStock
      );
      note.textContent = '';
    }
    
    optionLabel.classList.remove(
      this.classes.labelDisabled,
      this.classes.labelUnavailable,
      this.classes.labelOutOfStock
    );
    optionLabel.removeAttribute('hidden');
    
    // If dropdown is open, ensure tabindex is set correctly for keyboard navigation
    if (this.isOpen) {
      optionLabel.tabIndex = 0;
    }
  }

  disableHost() {
    this.isDisabled = true;
    if (this.trigger) {
      this.trigger.disabled = true;
    }
    this.hideNote();
    if (this.valueHolder && this.option) {
      this.valueHolder.textContent = `No available ${this.option.name.toLowerCase()}`;
    }
  }

  reenableHost() {
    this.isDisabled = false;
    if (this.trigger) {
      this.trigger.disabled = false;
    }
    this.showCurrentValue();
    this.showNote();
  }

  updateShopCollectionForm() {
    // Fixes z-index issues with shop collection section dropdowns
    const shopCollectionForm = this.host.closest('[shop-collection-form]');
    if (!shopCollectionForm) {
      return;
    }
    
    const formHasOpenSelector = !!shopCollectionForm.querySelector(`.${this.classes.open}`);
    
    if (formHasOpenSelector) {
      shopCollectionForm.style.zIndex = '3';
    } else {
      shopCollectionForm.style.zIndex = '0';
    }
  }

  /**
   * Recalculate option availability based on potential variants
   * @param {Array} potentialVariants - Array of variants matching current selection
   */
  recalculate(potentialVariants) {
    if (this.isIndependent || this.noCalculate || !this.option) {
      return;
    }
    
    if (!potentialVariants || potentialVariants.length === 0) {
      // If no potential variants provided, don't disable everything - just return
      return;
    }
    
    const optionKey = `option${this.option.position}`;
    const optionIndex = this.option.position - 1; // 0-based index for options array
    const currentValue = this.getValue();
    let valueToSet;
    
    this.options.forEach(option => {
      const input = option.querySelector('input[type="radio"]');
      const value = input?.value;
      
      if (!value) return;
      
      // Find all variants that have this option value at this position
      // Use both optionKey (option1, option2, etc.) and options array for compatibility
      const variantsWithThisOption = potentialVariants.filter(variant => {
        // Check using optionKey (e.g., "option2")
        if (variant[optionKey]) {
          return variant[optionKey] === value;
        }
        // Fallback to options array
        if (variant.options && variant.options[optionIndex] !== undefined) {
          return variant.options[optionIndex] === value;
        }
        return false;
      });
      
      if (variantsWithThisOption.length > 0) {
        // Variant exists for this option
        // Check if all variants with this option are unavailable
        const allUnavailable = variantsWithThisOption.every(v => !v.available);
        
        if (allUnavailable) {
          // All variants are out of stock
          this.disableOption(option, false, true);
        } else {
          // At least one variant is available
          this.enableOption(option);
          
          // Set this as the value to select if current value is invalid
          if (!valueToSet) {
            const firstAvailable = variantsWithThisOption.find(v => v.available);
            if (firstAvailable) {
              valueToSet = value;
            }
          }
        }
      } else {
        // No variant exists for this option value (unavailable)
        this.disableOption(option, true, false);
      }
    });
    
    // If current value is not valid, select the first available
    const currentValueVariants = potentialVariants.filter(v => {
      if (v[optionKey]) {
        return v[optionKey] === currentValue;
      }
      if (v.options && v.options[optionIndex] !== undefined) {
        return v.options[optionIndex] === currentValue;
      }
      return false;
    });
    
    if (currentValueVariants.length === 0 && valueToSet) {
      const input = this.host.querySelector(`input[type="radio"][value="${valueToSet}"]`);
      if (input && !input.disabled) {
        // Guard: verify the new combination would produce a valid variant before switching.
        // Without this, the auto-switch can fire a change event that resolves to null,
        // which clears the price and shows "Unavailable" on initial PDP load.
        const variantPicker = this.host.closest('variant-picker');
        if (variantPicker && variantPicker.currentOptions && variantPicker.variants) {
          const newOptions = [...variantPicker.currentOptions];
          newOptions[this.option.position - 1] = valueToSet;
          const wouldBeValid = variantPicker.variants.some(v =>
            v.options.every((opt, i) =>
              newOptions[i] === undefined || newOptions[i] === null || newOptions[i] === opt
            )
          );
          if (!wouldBeValid) {
            return; // Skip — switching here would break price and ATC display
          }
        }

        input.checked = true;
        // Trigger change event to update variant picker
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    
    // Check if all options are disabled
    const allOptionsAreDisabled = Array.from(this.options).every(opt => 
      opt.hasAttribute('hidden')
    );
    
    if (allOptionsAreDisabled) {
      this.disableHost();
      return;
    } else if (this.isDisabled) {
      this.reenableHost();
    }
    
    // Only update display if dropdown is NOT currently open
    // If dropdown is open, don't interfere with the open state
    if (!this.isOpen) {
      this.showCurrentValue();
      this.showNote();
    }
  }
}

/**
 * Initialize all select-product-option elements on the page
 */
function initializeSelectProductOptionHandlers() {
  const selectOptions = document.querySelectorAll('[select-product-option]');
  
  selectOptions.forEach(element => {
    // Don't re-initialize if handler already exists
    if (element.selectProductOptionHandler) return;
    
    const handler = new SelectProductOptionHandler(element);
    element.selectProductOptionHandler = handler;
    
    // If this element is inside a variant-picker, add it to the dynamicSelects array
    const variantPicker = element.closest('variant-picker');
    if (variantPicker && variantPicker.dynamicSelects && !variantPicker.dynamicSelects.includes(handler)) {
      variantPicker.dynamicSelects.push(handler);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSelectProductOptionHandlers);
} else {
  initializeSelectProductOptionHandlers();
}

// Re-initialize on section load (Shopify Sections API)
document.addEventListener('shopify:section:load', initializeSelectProductOptionHandlers);

// Re-initialize on AJAX loads
document.addEventListener('ajax:complete', initializeSelectProductOptionHandlers);

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SelectProductOptionHandler;
}

// Export to window for cross-script access
if (typeof window !== 'undefined') {
  window.SelectProductOptionHandler = SelectProductOptionHandler;
}

